// The paralegal's pre-GC intake console: GROUPS (big label + caret) → sub-groups
// (rounded-rect boxes, optional foot actions like "Ask GC") → items (members) → a
// provenance note → controls (Accept / Send Gmail); items may nest children and may
// be links. Shared between Main's pane-3 and the MemoCC page. Accept flips the item
// label amber→dark-green (LOCAL for now; persistence + Gmail/round-trip are next).
// Ken 2026-10-10.
import { useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { artifacts, type CommEvent, type ReplyCandidate } from '../api'

// Sandbox safety net (Ken 2026-10-11): while we're testing on real memos, every
// Gmail compose we open is pre-filled with a "please ignore" disclaimer in CASE it
// gets sent by accident. Flip to false when the loop goes to production.
const SANDBOX_SAFETY = true
const SANDBOX_BODY = 'Test email from OGC — please ignore.'
// Manual "+ Link a reply" is hidden (Ken 2026-10-11): too much hand-work, and we're
// automating the linkage. Inbound events still RENDER — this only hides the manual
// affordance. Flip true to bring the hand-link back.
const SHOW_MANUAL_LINK = false

// Open a Gmail compose in a new tab, addressed to the given recipients. The app never
// sends: the paralegal writes + sends from her own Gmail (respects the no-outbound rule).
function openGmailCompose(recipients: ApprovalRecipient[], subject: string) {
  const to = recipients.filter((r) => !r.disabled && r.email).map((r) => r.email).join(',')
  const params = new URLSearchParams({ view: 'cm', fs: '1', to, su: subject || 'Contract review' })
  if (SANDBOX_SAFETY) params.set('body', SANDBOX_BODY)
  window.open(`https://mail.google.com/mail/?${params.toString()}`, '_blank', 'noopener')
}

function fmtWhen(iso: string | null) {
  if (!iso) return ''
  const d = new Date(iso)
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export type ApprovalNote = { text: string; source: string; quote?: boolean }
export type ApprovalRecipient = { label: string; kind: string; email?: string; disabled?: boolean }
export type ApprovalButton = { id: string; label: string; kind: string; recipients?: ApprovalRecipient[]; disabled?: boolean }
export type ApprovalItem = {
  key: string; label: string; status?: string; note: ApprovalNote; why?: string; label_byline?: string
  buttons: ApprovalButton[]; children?: ApprovalItem[]; link?: string; tip?: string
}
export type ApprovalSubgroup = { key: string; label: string; items: ApprovalItem[]; actions?: ApprovalButton[] }
export type ApprovalGroup = { key: string; label: string; items?: ApprovalItem[]; subgroups?: ApprovalSubgroup[] }
export type ChatLine = { role: string; text: string }
export type ApprovalsData = { groups?: ApprovalGroup[]; footer_actions?: ApprovalButton[]; transcript?: ChatLine[] }

const AMBER = '#d4a017'   // a tad yellower
const GREEN = '#15803d'
const BTN = 'rounded-md px-3 py-1.5 text-[13px] font-medium'
const BTN_STYLE = { background: 'var(--me-panel)', border: '1px solid var(--me-border)', color: 'var(--me-text)' }

function sourceLabel(note: ApprovalNote) {
  return note.source === 'chat' ? 'intake chat'
    : note.source === 'email' ? 'from email'
    : note.source === 'document' ? 'from the agreement'
    : note.source === 'system' ? 'Secretary'
    : ''
}

function PlainButton({ b, onClick }: { b: ApprovalButton; onClick: () => void }) {
  return (
    <button disabled={b.disabled} onClick={b.disabled ? undefined : onClick} className={BTN}
      style={b.disabled ? { ...BTN_STYLE, opacity: 0.4, cursor: 'not-allowed' } : BTN_STYLE}>
      {b.label}
    </button>
  )
}

type ItemCtx = {
  accepted: Set<string>; toggleAccept: (p: string) => void; unaccept: (p: string) => void
  picker: string | null; setPicker: (p: string | null) => void
  propose: string | null; setPropose: (p: string | null) => void
  proposeDate: Record<string, string>; setProposeDate: (m: Record<string, string>) => void
  proposeChoice: Record<string, string>; setProposeChoice: (m: Record<string, string>) => void
  onStub: (s: string) => void
  comm: Record<string, CommEvent[]>                                   // trail keyed by unit_key
  sendGmail: (unitKey: string, unitLabel: string, rcpts: ApprovalRecipient[]) => void
  canLink: boolean                                                    // live memo → reply-linking on
  replyCandidates: ReplyCandidate[]; loadReplyCandidates: () => void
  linkReply: (unitKey: string, unitLabel: string, cardId: number) => void
}

function fmtDate(iso: string) {
  const p = iso.split('-').map(Number)
  if (p.length !== 3 || p.some(isNaN)) return iso
  return new Date(p[0], p[1] - 1, p[2]).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}

function Item({ path, item, ctx, child = false }: { path: string; item: ApprovalItem; ctx: ItemCtx; child?: boolean }) {
  const { accepted, toggleAccept, unaccept, picker, setPicker, propose, setPropose, proposeDate, setProposeDate, proposeChoice, setProposeChoice, onStub, comm, sendGmail, canLink, replyCandidates, loadReplyCandidates, linkReply } = ctx
  const trail = comm[item.key] || []
  const canComm = item.buttons.some((b) => b.kind === 'send_gmail')
  const replyPath = `${path}/reply`
  const proposePath = `${path}/propose`
  // A proposed new date (deadline unit) takes over the label as "our date" (amber), the
  // original date demoted below with its own tag; it can't be "accepted for now". (Ken 2026-10-11)
  const hasPropose = item.buttons.some((b) => b.kind === 'propose')
  const proposedISO = hasPropose ? proposeDate[path] : undefined
  const proposed = !!proposedISO
  const isAccepted = accepted.has(path) && !proposed
  const src = sourceLabel(item.note)
  const quoted = item.note.quote ?? (item.note.source === 'chat' || item.note.source === 'email')
  const dim = item.note.source === 'none' && !quoted
  // Accepted → black text + green ✓. Amber until accepted; a proposed date is amber.
  // status 'done' = green ✓ straight from the data (a forensic result like Format), no
  // click needed. (Ken 2026-10-11)
  const greenCheck = isAccepted || item.status === 'done'
  const labelColor = item.status === 'na' ? 'var(--me-textMute)'
    : (proposed || (item.status === 'pending' && !isAccepted)) ? AMBER
    : 'var(--me-text)'
  const labelCls = child ? 'text-[13px] font-medium' : 'text-[15px] font-medium'
  const mainLabel = proposed ? fmtDate(proposedISO!) : item.label
  const mainByline = proposed ? 'our date' : item.label_byline
  const small = { background: 'var(--me-panel)', border: '1px solid var(--me-border)', color: 'var(--me-text)' }
  const smallDisabled = { ...small, opacity: 0.4, cursor: 'not-allowed' as const }
  return (
    <div className={child ? 'mt-2 pl-3 border-l' : ''} style={child ? { borderColor: 'var(--me-border)' } : undefined}>
      {item.label && (
        <div className={labelCls} style={{ color: labelColor }} title={item.tip || undefined}>
          {greenCheck && <span style={{ color: GREEN }}>✓ </span>}
          {item.status === 'na' && <span style={{ color: 'var(--me-textMute)' }}>✓ </span>}
          {item.link
            ? <a href={item.link} style={{ color: labelColor, textDecoration: 'underline' }}>{mainLabel}</a>
            : mainLabel}
          {mainByline && <span className="text-xs ml-2 font-normal" style={{ color: 'var(--me-textMute)' }}>{mainByline}</span>}
        </div>
      )}
      {/* Keep the submitter's original requested date in view once we've proposed our own. */}
      {proposed && item.label && (
        <div className="text-[13px]" style={{ color: 'var(--me-textMute)' }}>
          {item.label}{item.label_byline && <span className="ml-2">{item.label_byline}</span>}
        </div>
      )}
      {/* WHY this is relevant — a plain line right under the label (no byline). */}
      {item.why && <div className="text-[13px] mt-0.5" style={{ color: 'var(--me-textMute)' }}>{item.why}</div>}
      {item.note.text && (
        <div className={child ? 'text-[13px]' : 'text-[14px]'} style={{ color: dim ? 'var(--me-textMute)' : 'var(--me-text)' }}>
          {quoted ? `“${item.note.text}”` : item.note.text}
          {src && <span className="text-xs ml-2" style={{ color: 'var(--me-textMute)' }}>{src}</span>}
        </div>
      )}
      {/* info / evidence (e.g. deadline breakdown, redlines/comments) renders BELOW the
          answer and ABOVE the buttons — part of the reasoning before acting. */}
      {(item.children || []).map((c) => <Item key={c.key} path={`${path}/${c.key}`} item={c} ctx={ctx} child />)}
      {item.buttons.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {item.buttons.map((b) => {
            if (b.kind === 'accept' || b.kind === 'accept_now') {
              const base = b.kind === 'accept_now' ? 'Accept for Now' : 'Accept'
              const done = b.kind === 'accept_now' ? 'Accepted for Now' : 'Accepted'
              // Once we've proposed our OWN date, "accept for now" no longer applies.
              const disabled = proposed && b.kind === 'accept_now'
              return (
                <button key={b.id} disabled={disabled} onClick={disabled ? undefined : () => toggleAccept(path)}
                  className={BTN} style={disabled ? { ...BTN_STYLE, opacity: 0.4, cursor: 'not-allowed' } : BTN_STYLE}>
                  {isAccepted ? done : base}
                </button>
              )
            }
            if (b.kind === 'send_gmail') {
              const pk = `${path}/gmail`
              return <button key={b.id} onClick={() => setPicker(picker === pk ? null : pk)} className={BTN} style={BTN_STYLE}>Send Gmail</button>
            }
            if (b.kind === 'propose') {
              return <button key={b.id} className={BTN} style={BTN_STYLE}
                onClick={() => { unaccept(path); setPropose(propose === proposePath ? null : proposePath) }}>{b.label}</button>
            }
            // accept_now + any other code-defined button → stub
            return <PlainButton key={b.id} b={b} onClick={() => onStub(`“${b.label}” — behavior comes next`)} />
          })}
        </div>
      )}
      {/* Send Gmail recipient sub-step — a short prioritized cast (never the GC). Picking
          a recipient opens a Gmail compose to THAT person (client-side) and logs the
          outreach; the reply comes back as an ordinary pane-2 card. Ken 2026-10-11. */}
      {picker === `${path}/gmail` && (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-[13px]">
          <span style={{ color: 'var(--me-textMute)' }}>Email:</span>
          {(item.buttons.find((b) => b.kind === 'send_gmail')?.recipients || []).map((r, i) => (
            <button key={`${r.kind}-${i}`} disabled={r.disabled}
              onClick={r.disabled ? undefined : () => sendGmail(item.key, item.label, [r])}
              className="rounded-md px-2.5 py-1 text-[12.5px]" style={r.disabled ? smallDisabled : small}>
              {r.label}
            </button>
          ))}
        </div>
      )}
      {/* Propose (New) Deadline — pick a date, then keep it internal or propose it out. */}
      {propose === proposePath && (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-[13px]">
          <input type="date" value={proposeDate[path] || ''}
            onChange={(e) => { unaccept(path); setProposeDate({ ...proposeDate, [path]: e.target.value }) }}
            className="rounded-md px-2 py-1" style={small} />
          {/* Keep / Propose — a selected toggle pair (mutually exclusive). Choosing one
              shows it selected and clears the other. Saving/sending comes next. (Ken 2026-10-11) */}
          {(['keep', 'propose'] as const).map((which) => {
            const label = which === 'keep' ? 'Keep between me & GC' : 'Propose to submitter'
            const on = proposeChoice[path] === which
            const sel = { background: AMBER, border: `1px solid ${AMBER}`, color: '#fff' }
            return (
              <button key={which} disabled={!proposeDate[path]} className="rounded-md px-2.5 py-1 text-[12.5px]"
                style={!proposeDate[path] ? smallDisabled : (on ? sel : small)}
                onClick={proposeDate[path] ? () => setProposeChoice({ ...proposeChoice, [path]: which }) : undefined}>
                {on ? `✓ ${label}` : label}
              </button>
            )
          })}
        </div>
      )}
      {/* Communication mini-thread for this unit — outbound asks (✉, Mistral stub of the
          ask; she writes the real email in her own Gmail) AND inbound replies (↩) the
          paralegal hand-linked from a pane-2 card, so pane-3 reflects the answer, not just
          the ask. Ken 2026-10-11. */}
      {(trail.length > 0 || (SHOW_MANUAL_LINK && canLink && canComm)) && (
        <div className="mt-2 space-y-1">
          {trail.map((ev) => (
            <div key={ev.id} className="text-[12px]" style={{ color: 'var(--me-textMute)' }}>
              {ev.direction === 'in' ? (
                <>
                  ↩ {ev.reply?.from_name ? `${ev.reply.from_name}: ` : ''}{ev.summary}
                  {(ev.reply?.source_date || ev.created_at) && <span> · {fmtWhen(ev.reply?.source_date || ev.created_at)}</span>}
                  {ev.reply && <a href={`/artifact/${ev.reply.id}`} className="ml-1" style={{ textDecoration: 'underline' }}>open</a>}
                </>
              ) : (
                <>✉ {ev.actor ? `${ev.actor} ${ev.summary.charAt(0).toLowerCase()}${ev.summary.slice(1)}` : ev.summary}{ev.created_at && <span> · {fmtWhen(ev.created_at)}</span>}{ev.is_sandbox && <span> · test</span>}</>
              )}
            </div>
          ))}
          {SHOW_MANUAL_LINK && canLink && canComm && (
            <div>
              <button className="text-[12px]" style={{ color: 'var(--me-textMute)', textDecoration: 'underline' }}
                onClick={() => { loadReplyCandidates(); setPicker(picker === replyPath ? null : replyPath) }}>
                + Link a reply
              </button>
              {picker === replyPath && (
                <div className="mt-1 space-y-1">
                  {replyCandidates.length === 0 && (
                    <div className="text-[12px]" style={{ color: 'var(--me-textMute)' }}>No cards to link yet.</div>
                  )}
                  {replyCandidates.map((c) => (
                    <button key={c.id} onClick={() => linkReply(item.key, item.label, c.id)}
                      className="block text-left rounded-md px-2.5 py-1 text-[12.5px]" style={small}>
                      {c.from_name || '(unknown)'} — {c.subject || '(no subject)'}{c.source_date && ` · ${fmtWhen(c.source_date)}`}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default function ApprovalsConsole(
  { data, artifactId, agreementName, comm: initialComm }:
  { data?: ApprovalsData; artifactId?: number; agreementName?: string; comm?: Record<string, CommEvent[]> },
) {
  const groups = data?.groups || []
  const transcript = data?.transcript || []
  const [txOpen, setTxOpen] = useState(false)
  // Groups start COLLAPSED (carets tucked in) so landing on the workspace shows the whole
  // outline at a glance; the paralegal expands what she needs. (Ken 2026-10-12)
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(
    () => Object.fromEntries((data?.groups || []).map((g) => [g.key, true])),
  )
  const [accepted, setAccepted] = useState<Set<string>>(new Set())
  const [picker, setPicker] = useState<string | null>(null)
  const [propose, setPropose] = useState<string | null>(null)
  const [proposeDate, setProposeDate] = useState<Record<string, string>>({})
  const [proposeChoice, setProposeChoice] = useState<Record<string, string>>({})
  const [comm, setComm] = useState<Record<string, CommEvent[]>>(initialComm || {})
  const [replyCandidates, setReplyCandidates] = useState<ReplyCandidate[]>([])
  const [candidatesLoaded, setCandidatesLoaded] = useState(false)
  const [hint, setHint] = useState('')
  if (!groups.length) return null
  const toggleAccept = (p: string) => setAccepted((s) => { const n = new Set(s); n.has(p) ? n.delete(p) : n.add(p); return n })
  const unaccept = (p: string) => setAccepted((s) => { const n = new Set(s); n.delete(p); return n })
  // Open a Gmail compose to the chosen recipient(s) and log the outreach so the unit's
  // trail shows it. The app never sends — Maria sends from her own Gmail.
  const sendGmail = async (unitKey: string, unitLabel: string, rcpts: ApprovalRecipient[]) => {
    openGmailCompose(rcpts, agreementName || '')
    setPicker(null)
    if (!artifactId) { setHint('Opened a Gmail draft (trail logging needs the live memo).'); return }
    try {
      const ev = await artifacts.logContractComm(artifactId, {
        unit_key: unitKey, unit_label: unitLabel,
        recipients: rcpts.filter((r) => !r.disabled).map((r) => ({ label: r.label, email: r.email })),
        agreement_name: agreementName, sandbox: SANDBOX_SAFETY,
      })
      setComm((prev) => ({ ...prev, [unitKey]: [...(prev[unitKey] || []), ev] }))
    } catch {
      setHint('Opened the draft, but logging the outreach failed.')
    }
  }
  // Load the pane-2 cards that could be a reply (once per open console).
  const loadReplyCandidates = async () => {
    if (!artifactId || candidatesLoaded) return
    setCandidatesLoaded(true)
    try { setReplyCandidates(await artifacts.replyCandidates(artifactId)) } catch { /* leave empty */ }
  }
  // Hand-link a pane-2 card as the inbound reply on a unit → appends to that unit's thread.
  const linkReply = async (unitKey: string, unitLabel: string, cardId: number) => {
    setPicker(null)
    if (!artifactId) return
    try {
      const ev = await artifacts.linkReply(artifactId, { unit_key: unitKey, unit_label: unitLabel, reply_artifact_id: cardId })
      setComm((prev) => ({ ...prev, [unitKey]: [...(prev[unitKey] || []), ev] }))
    } catch {
      setHint('Could not link that reply.')
    }
  }
  const ctx: ItemCtx = { accepted, toggleAccept, unaccept, picker, setPicker, propose, setPropose, proposeDate, setProposeDate, proposeChoice, setProposeChoice, onStub: setHint, comm, sendGmail, canLink: !!artifactId, replyCandidates, loadReplyCandidates, linkReply }

  return (
    <div className="mb-6">
      {groups.map((g) => {
        const isOpen = !collapsed[g.key]
        return (
          <div key={g.key} className="rounded-xl p-4 mb-4"
            style={{ background: 'var(--me-surface)', border: '1px solid var(--me-border)' }}>
            <button onClick={() => setCollapsed((c) => ({ ...c, [g.key]: isOpen }))}
              className="flex items-center gap-1.5 w-full text-left">
              {isOpen ? <ChevronDown size={18} style={{ color: 'var(--me-textMute)' }} />
                : <ChevronRight size={18} style={{ color: 'var(--me-textMute)' }} />}
              <span className="text-lg font-semibold">{g.label}</span>
            </button>
            {/* everything inside aligns under the first word of the label (past the caret) */}
            {isOpen && (
              <div className="mt-3 space-y-4" style={{ paddingLeft: 24 }}>
                {/* direct items (spelled-out units) first, then the rounded-rect boxes */}
                {(g.items || []).map((it) => <Item key={it.key} path={`${g.key}/${it.key}`} item={it} ctx={ctx} />)}
                {(g.subgroups || []).map((sg) => (
                  <div key={sg.key} className="rounded-lg p-3" style={{ border: '1px solid var(--me-border)' }}>
                    <div className="text-[12px] font-semibold mb-2.5" style={{ color: 'var(--me-textMute)', letterSpacing: '0.03em' }}>
                      {sg.label}
                    </div>
                    <div className="space-y-4">
                      {sg.items.map((it) => <Item key={it.key} path={`${g.key}/${sg.key}/${it.key}`} item={it} ctx={ctx} />)}
                    </div>
                    {(sg.actions || []).length > 0 && (
                      <div className="mt-3 pt-3 flex flex-wrap gap-2" style={{ borderTop: '1px solid var(--me-border)' }}>
                        {sg.actions!.map((a) => (
                          <PlainButton key={a.id} b={a} onClick={() => setHint(`“${a.label}” — behavior comes next`)} />
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      })}
      {/* Chat Transcript — the submitter chat verbatim, as colored bubbles (replaces the
          old checklist memo). Collapsed by default; the console is the primary surface. */}
      {transcript.length > 0 && (
        <div className="rounded-xl p-4 mb-4" style={{ background: 'var(--me-surface)', border: '1px solid var(--me-border)' }}>
          <button onClick={() => setTxOpen((o) => !o)} className="flex items-center gap-1.5 w-full text-left">
            {txOpen ? <ChevronDown size={18} style={{ color: 'var(--me-textMute)' }} />
              : <ChevronRight size={18} style={{ color: 'var(--me-textMute)' }} />}
            <span className="text-lg font-semibold">Chat Transcript</span>
          </button>
          {txOpen && (
            <div className="mt-3 space-y-2" style={{ paddingLeft: 24 }}>
              {transcript.map((m, i) => (
                <div key={i} className={m.role === 'submitter' ? 'flex justify-end' : 'flex justify-start'}>
                  <div className="rounded-2xl px-3 py-2 text-[13px] whitespace-pre-wrap" style={m.role === 'submitter'
                    ? { background: 'var(--me-accent, #2563eb)', color: '#fff', maxWidth: '80%' }
                    : { background: 'var(--me-panel)', border: '1px solid var(--me-border)', color: 'var(--me-text)', maxWidth: '80%' }}>
                    {m.text}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {/* console-wide footer actions (Ask GC / Ask Secretary) removed per Ken 2026-10-12. */}
      {hint && <div className="text-xs mt-2" style={{ color: 'var(--me-textMute)' }}>{hint}</div>}
    </div>
  )
}
